import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, AlertTriangle, Phone, Mail, Briefcase, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";

const docColor = { valid: "text-green-600", expiring: "text-amber-600", expired: "text-rose-600", unknown: "text-slate-400" };

export function AssignContractorDialog({ open, onOpenChange, workOrders, onDone }) {
  const { user } = useAuth();
  const canEditFinancials = user?.role === "admin";
  const ids = (workOrders || []).map((w) => w.id);
  const isBulk = ids.length > 1;
  const single = ids.length === 1 ? workOrders[0] : null;

  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [cid, setCid] = useState("unassigned");
  const [payout, setPayout] = useState("");
  const [sdate, setSdate] = useState("");
  const [stime, setStime] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [zeroConfirm, setZeroConfirm] = useState(false);
  const [pendingOffer, setPendingOffer] = useState(false);

  useEffect(() => {
    if (!open || ids.length === 0) return;
    setLoading(true);
    setCid(single?.assigned_contractor_id || "unassigned");
    setPayout(single?.contractor_payout || "");
    setSdate(single?.scheduled_date || "");
    setStime(single?.scheduled_time || "");
    setNotes("");
    const req = isBulk
      ? api.post("/work-orders/bulk-recommendations", { work_order_ids: ids })
      : api.get(`/work-orders/${single.id}/recommendations`);
    req.then(({ data }) => {
      setRecs(data.recommendations || []);
      setMeta(isBulk ? data : null);
    }).catch(() => setRecs([])).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [open, ids.join(",")]);

  const submit = async (sendOffer, confirmedZero = false) => {
    const assigning = cid !== "unassigned";
    if (sendOffer && !assigning) return toast.error("Select a contractor to send an offer");
    if (canEditFinancials && assigning) {
      if (payout === "" || isNaN(Number(payout)) || Number(payout) < 0) return toast.error("Enter a valid contractor payout");
      if (Number(payout) === 0 && !confirmedZero) { setPendingOffer(sendOffer); setZeroConfirm(true); return; }
    }
    setBusy(true);
    try {
      const payload = {
        work_order_ids: ids, assign_contractor: true,
        contractor_id: assigning ? cid : null,
        scheduled_date: sdate || null, scheduled_time: stime || null,
        notes: notes || null, send_offer: sendOffer,
      };
      if (canEditFinancials && payout !== "") payload.contractor_payout = Number(payout);
      const { data } = await api.post("/work-orders/bulk-assign", payload);
      if (sendOffer) toast.success(isBulk ? `Offer sent for ${data.offered} work orders.` : "Assignment offer sent.");
      else if (cid === "unassigned") toast.success(isBulk ? `${data.assigned} work orders unassigned.` : "Work order unassigned.");
      else toast.success(isBulk ? `${data.assigned} work orders assigned successfully.` : "Contractor assigned successfully.");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign"); }
    setBusy(false);
  };

  const eligible = recs.filter((r) => r.eligible);
  const others = recs.filter((r) => !r.eligible);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="assign-contractor-modal">
        <DialogHeader>
          <DialogTitle>{isBulk ? `Assign Contractor — ${ids.length} Work Orders` : `Assign Contractor — ${single?.wo_number}`}</DialogTitle>
          <DialogDescription>Pick a qualified contractor, then assign directly or send an offer.</DialogDescription>
        </DialogHeader>

        {/* Summary */}
        {single && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm bg-slate-50 rounded-lg p-3" data-testid="assign-summary">
            <Sum k="Trade" v={single.service_category} />
            <Sum k="Urgency" v={single.urgency} />
            <Sum k="Property" v={single.property?.name} />
            <Sum k="Location" v={`${single.property?.city || ""}${single.property?.state ? ", " + single.property.state : ""}`} />
            <Sum k="Current" v={single.contractor ? `${single.contractor.first_name} ${single.contractor.last_name}` : "Unassigned"} />
            <Sum k="Scheduled" v={single.scheduled_date || "—"} />
            <Sum k="Client Price" v={money(single.customer_price)} />
            <Sum k="Contractor Cost" v={money(single.contractor_payout)} />
            <Sum k="Gross Profit" v={`${money(single.gross_profit)} · ${single.gross_margin || 0}%`} />
          </div>
        )}
        {isBulk && meta && (
          <div className="space-y-2" data-testid="assign-bulk-summary">
            {(meta.mixed_trade || meta.mixed_state) && (
              <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="assign-mixed-warning">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Selected work orders have {meta.mixed_trade ? `different trades (${meta.trades.join(", ")})` : ""}{meta.mixed_trade && meta.mixed_state ? " and " : ""}{meta.mixed_state ? `different states (${meta.states.join(", ")})` : ""}. Only contractors qualified for all are eligible.</span>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 text-sm bg-slate-50 rounded-lg p-3">
              <Sum k="Combined Revenue" v={money(meta.combined.revenue)} />
              <Sum k="Combined Cost" v={money(meta.combined.contractor_cost + meta.combined.material_cost)} />
              <Sum k="Projected GP" v={`${money(meta.combined.gross_profit)} · ${meta.combined.margin}%`} />
            </div>
          </div>
        )}

        {/* Contractor selection */}
        <div className="space-y-2">
          <Label>Select Contractor</Label>
          {loading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div> : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="assign-contractor-list">
              <label className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer ${cid === "unassigned" ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}>
                <input type="radio" name="con" checked={cid === "unassigned"} onChange={() => setCid("unassigned")} data-testid="assign-unassigned" />
                <span className="text-sm text-slate-600">Leave Unassigned</span>
              </label>
              {[...eligible, ...others].map((r) => (
                <label key={r.contractor_id} data-testid={`assign-option-${r.contractor_id}`}
                  className={`block rounded-lg border p-3 cursor-pointer ${!r.eligible ? "opacity-60" : ""} ${cid === r.contractor_id ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}>
                  <div className="flex items-start gap-2">
                    <input type="radio" name="con" className="mt-1" checked={cid === r.contractor_id} disabled={!r.eligible} onChange={() => setCid(r.contractor_id)} data-testid={`assign-radio-${r.contractor_id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 text-sm">{r.name || r.company_name}</span>
                        {r.match != null && <span className="text-indigo-600 font-semibold text-sm">— {r.match}%</span>}
                        <span className={`text-xs font-medium ${docColor[r.doc_status] || "text-slate-400"}`}>docs: {r.doc_status}</span>
                        {r.emergency_availability && <span className="text-xs text-green-600 flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> available</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {r.trade}{r.secondary_trades ? `, ${r.secondary_trades}` : ""}</span>
                        {r.state && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.state}</span>}
                        <span>{r.workload} active jobs</span>
                        {r.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                        {r.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</span>}
                      </div>
                      {r.warnings?.length > 0 && <div className="text-xs text-amber-600 mt-0.5">{r.warnings.join(" · ")}</div>}
                    </div>
                  </div>
                </label>
              ))}
              {recs.length === 0 && <p className="text-sm text-slate-400 py-2">No eligible contractors found.</p>}
            </div>
          )}
        </div>

        {/* Assignment fields */}
        <div className="grid grid-cols-2 gap-3">
          {canEditFinancials && <div><Label>Contractor Payout {isBulk ? "(each)" : ""}</Label><Input data-testid="assign-payout" type="number" value={payout} onChange={(e) => setPayout(e.target.value)} className="mt-1.5" /></div>}
          <div><Label>Scheduled Date</Label><Input data-testid="assign-date" type="date" value={sdate} onChange={(e) => setSdate(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Scheduled Time</Label><Input data-testid="assign-time" type="time" value={stime} onChange={(e) => setStime(e.target.value)} className="mt-1.5" /></div>
        </div>
        <div><Label>Assignment Notes</Label><Textarea data-testid="assign-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" /></div>

        {zeroConfirm && (
          <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="zero-payout-confirm">
            <span className="text-amber-800">Contractor payout is $0. Continue?</span>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => setZeroConfirm(false)}>Cancel</Button>
              <Button size="sm" data-testid="confirm-zero-payout" className="bg-amber-600 hover:bg-amber-700" onClick={() => { setZeroConfirm(false); submit(pendingOffer, true); }}>Continue</Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="sm:mr-auto">Cancel</Button>
          <Button data-testid="assign-offer-btn" variant="outline" disabled={busy || loading} onClick={() => submit(true)} className="border-blue-200 text-blue-700 hover:bg-blue-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Assignment Offer"}
          </Button>
          <Button data-testid="assign-direct-btn" disabled={busy || loading} onClick={() => submit(false)} className="bg-blue-600 hover:bg-blue-700">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assign Directly"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Sum = ({ k, v }) => <div><span className="text-slate-400">{k}: </span><span className="text-slate-800 font-medium">{v || "—"}</span></div>;
